import * as React from "react"

import { cn } from "@/lib/utils"

import { StoneBorderRaster } from "./border-raster"
import { StoneFill, StoneTextureLayer } from "./fill"
import { stoneTones, type StoneTone } from "./tones"

export type { StoneTone } from "./tones"

// Host must be a positioned box. --stone-cap is the corner cell of the 9-slice: 12px renders the
// Figma export 1:1 (2px stroke, 10px radius); the fill radius follows it (10/12 of the cap).
// Override per consumer with `[--stone-cap:9px]` to scale the whole frame down.
export const stoneHostClass =
  "relative isolate [--stone-cap:12px] [--stone-radius:calc(var(--stone-cap)*10/12)]"

export type StoneClip = "round" | "sheet"

type StoneLayersProps = {
  tone: StoneTone
  clip?: StoneClip
  raster?: boolean
}

const stoneClipClass: Record<StoneClip, string> = {
  round: "rounded-(--stone-radius)",
  sheet: "rounded-t-(--stone-radius) rounded-b-none",
}

const stoneFillClipClass: Record<StoneClip, string> = {
  round: "rounded-[calc(var(--stone-radius)-1px)]",
  sheet: "rounded-t-[calc(var(--stone-radius)-1px)] rounded-b-none",
}

const stoneFillFlushClass: Record<StoneClip, string> = {
  round: "inset-0 rounded-(--stone-radius)",
  sheet: "inset-0 rounded-t-(--stone-radius) rounded-b-none",
}

// Fill + texture + border, clipped to the rounded rect. Figma order is fill → stroke → texture,
// but the stroke asset is cut from the export with the texture already baked into its pixels, so
// here the texture grains the fill only and the border goes on top. For hosts that are already
// the box (e.g. <button>); content must be `relative z-10`.
// `sheet` keeps the top radius and cuts the bottom at 90°.
export function StoneLayers({
  tone,
  clip = "round",
  raster = true,
}: StoneLayersProps) {
  const skin = stoneTones[tone]

  return (
    <span
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        stoneClipClass[clip],
      )}
    >
      <StoneFill
        skin={skin}
        className={cn(
          raster ? stoneFillClipClass[clip] : stoneFillFlushClass[clip],
          tone === "header" &&
            "shadow-[inset_0_4px_10px_0_--theme(--color-black-1000/35%)]",
        )}
      />
      <StoneTextureLayer texture={skin.texture} />
      {raster ? (
        <StoneBorderRaster
          className={clip === "sheet" ? "-bottom-(--stone-cap)" : undefined}
        />
      ) : (
        <span
          className={cn(
            "absolute inset-0 border-2 border-white/10",
            stoneClipClass[clip],
          )}
        />
      )}
    </span>
  )
}

export type StoneFrameProps = React.ComponentPropsWithoutRef<"div"> & {
  tone?: StoneTone
  clip?: StoneClip
  raster?: boolean
  contentClassName?: string
}

// Plate: wraps arbitrary content in the stone frame.
export const StoneFrame = React.forwardRef<HTMLDivElement, StoneFrameProps>(
  (
    {
      tone = "dark",
      clip = "round",
      raster = true,
      className,
      contentClassName,
      children,
      ...props
    },
    ref,
  ) => (
    <div ref={ref} className={cn(stoneHostClass, className)} {...props}>
      <StoneLayers tone={tone} clip={clip} raster={raster} />
      <div className={cn("relative z-10", contentClassName)}>{children}</div>
    </div>
  ),
)
StoneFrame.displayName = "StoneFrame"
